import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { config } from "../config/env";

// Function to get auth token from localStorage
const getAuthToken = (): string | null => {
  try {
    const authStore = localStorage.getItem('bike-tracker-auth');
    if (authStore) {
      const parsed = JSON.parse(authStore);
      return parsed?.state?.token || null;
    }
    return null;
  } catch {
    return null;
  }
};

interface UseWebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  reconnectAttempts?: number;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const {
    url = config.websocketUrl,
    autoConnect = true,
    reconnectAttempts = 5,
  } = options;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect]);

  const connect = () => {
    if (socketRef.current?.connected) return;

    if (config.enableDebugLogs) {
      console.log(`🔌 Connecting to WebSocket: ${url}`);
    }

    const token = getAuthToken();

    socketRef.current = io(url, {
      reconnectionAttempts: reconnectAttempts,
      timeout: 5000,
      transports: ["websocket", "polling"],
      upgrade: true,
      rememberUpgrade: true,
      auth: token ? { token } : undefined,
    });

    socketRef.current.on("connect", () => {
      setConnected(true);
      setReconnecting(false);
      if (config.enableDebugLogs) {
        console.log("✅ WebSocket connected successfully");
      }
    });

    socketRef.current.on("disconnect", (reason) => {
      setConnected(false);
      if (config.enableDebugLogs) {
        console.log("❌ WebSocket disconnected:", reason);
      }
    });

    socketRef.current.on("connect_error", (error) => {
      if (config.enableDebugLogs) {
        console.error("🚫 WebSocket connection error:", error);
      }
    });

    socketRef.current.on("reconnect_attempt", (attemptNumber) => {
      setReconnecting(true);
      if (config.enableDebugLogs) {
        console.log(`🔄 WebSocket reconnection attempt #${attemptNumber}`);
      }
    });

    socketRef.current.on("reconnect", (attemptNumber) => {
      setConnected(true);
      setReconnecting(false);
      if (config.enableDebugLogs) {
        console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
      }
    });

    socketRef.current.on("reconnect_failed", () => {
      setReconnecting(false);
      if (config.enableDebugLogs) {
        console.error("💥 WebSocket reconnection failed after all attempts");
      }
    });
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
      setReconnecting(false);
    }
  };

  const emit = (event: string, data?: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  };

  const on = (event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  };

  const off = (event: string, callback?: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.off(event, callback);
    }
  };

  return {
    connected,
    reconnecting,
    connect,
    disconnect,
    emit,
    on,
    off,
  };
}
